import './CommerceOps.scss';
import * as Dfns from 'date-fns/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdOutlineMenuBook } from 'react-icons/md';
import { useQuery } from '@tanstack/react-query';
import { CommercePartnerAccount } from '@blisspointmedia/bpm-types/dist/CommerceOps';
import {
  DATE_FORMAT,
  MONDAY_OF_LAST_WEEK
} from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import { capitalizeString } from '@blisspointmedia/bpm-types/dist/StyleUtils';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker';
import { Background, Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { EmptyState } from '@Components/EmptyState';
import { GlossaryModal } from '@Components/GlossaryModal/GlossaryModal';
import { Page } from '@Components/Page';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { FullPageSpinner } from '@Components/Spinner';
import { useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useTabbedNav } from '@src/utils/hooks/useNav';
import { DateRange } from '@src/Components/DatePicker/date-picker-types';
import { Buybox } from './Buybox';
import {
  BuyboxData,
  commerceOpsTableDownload,
  fetchBuyboxData,
  fetchCommerceClients,
  fetchInventoryData,
  fetchKeywordData,
  fetchOverviewData,
  GLOSSARY,
  InventoryData,
  KeywordData,
  NAVS,
  OverviewData,
  PageTab,
  PARTNER_TYPE_UI_CONFIG_MAP
} from './CommerceOpsUtils';
import { Inventory } from './Inventory';
import { Keyword } from './Keyword';
import { Overview } from './Overview';

// helper that handles the common pattern of: parse ISO -> adjust date -> format
const adjustStrDate = (
  strDate: string,
  adjustFn: (n: number, date: Date | number) => Date,
  amount: number
): string => {
  const parsedDate = Dfns.parseISO(strDate);
  const adjustedDate = adjustFn(amount, parsedDate);
  return Dfns.format(DATE_FORMAT, adjustedDate);
};

const defaultPrimaryDateRange = {
  start: adjustStrDate(MONDAY_OF_LAST_WEEK, Dfns.subWeeks, 3),
  end: adjustStrDate(MONDAY_OF_LAST_WEEK, Dfns.addDays, 6)
};

const defaultComparisonDateRange = {
  start: adjustStrDate(defaultPrimaryDateRange.start, Dfns.subWeeks, 4),
  end: adjustStrDate(defaultPrimaryDateRange.start, Dfns.subDays, 1)
};

const noop = () => undefined;

const TAB_FETCHERS = {
  [PageTab.OVERVIEW]: fetchOverviewData,
  [PageTab.INVENTORY]: fetchInventoryData,
  [PageTab.KEYWORDS_AND_TRAFFIC]: fetchKeywordData,
  [PageTab.BUYBOX]: fetchBuyboxData
};

const CommerceOps = () => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();
  const [selectedClientName, setSelectedClientName] = useState<string>();
  const [selectedPartnerType, setSelectedPartnerType] = useState<CommercePartnerAccount>();

  const {
    data: commerceClientOptions = [],
    isLoading: isFetchingAccount,
    error: clientsError
  } = useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['commerce-ops-clients', cid],
    queryFn: () => fetchCommerceClients(cid)
  });

  useEffect(() => {
    if (clientsError) {
      setError({ message: 'Failed to load commerce accounts', reportError: clientsError });
    }
  }, [clientsError, setError]);

  useEffect(() => {
    if (selectedClientName || commerceClientOptions.length === 0) {
      return;
    }
    setSelectedClientName(commerceClientOptions[0].clientName);
    setSelectedPartnerType(commerceClientOptions[0].partnerAccounts[0]);
  }, [commerceClientOptions, selectedClientName]);

  const selectedCommerceClient = useMemo(
    () => commerceClientOptions.find(client => client.clientName === selectedClientName),
    [commerceClientOptions, selectedClientName]
  );

  const availablePartnerAccounts = useMemo(
    () =>
      selectedCommerceClient?.partnerAccounts.map(acct => ({
        key: acct.partnerType,
        label: capitalizeString(acct.partnerType)
      })) ?? [],
    [selectedCommerceClient]
  );

  const onToggle = useCallback(
    (key: string) => {
      setSelectedPartnerType(
        selectedCommerceClient?.partnerAccounts.find(acct => acct.partnerType === key)
      );
    },
    [selectedCommerceClient]
  );

  const showPartnerAccountToggle = availablePartnerAccounts.length > 1;
  const clientNames = useMemo(
    () => commerceClientOptions.map(client => client.clientName),
    [commerceClientOptions]
  );
  const showSubheader = commerceClientOptions.length > 1 || showPartnerAccountToggle;

  const uiConfig = selectedPartnerType
    ? PARTNER_TYPE_UI_CONFIG_MAP[selectedPartnerType.partnerType]
    : null;

  const disabledKeys = useMemo(
    () =>
      new Set(
        uiConfig ? NAVS.filter(nav => uiConfig.tabs[nav.key].disabled).map(nav => nav.key) : []
      ),
    [uiConfig]
  );

  const { tab, goToTab } = useTabbedNav({
    baseURL: 'commerce-ops',
    defaultKey: PageTab.OVERVIEW,
    navs: NAVS.map(nav => nav.key)
  });

  const handleGoToTab = useCallback(
    (key: string) => {
      goToTab(disabledKeys.has(key as PageTab) ? PageTab.OVERVIEW : (key as PageTab));
    },
    [disabledKeys, goToTab]
  );

  useEffect(() => {
    if (uiConfig && disabledKeys.has(tab)) {
      goToTab(PageTab.OVERVIEW);
    }
  }, [tab, disabledKeys, uiConfig, goToTab]);

  const navRenderer = useCallback(
    (label: string) => {
      const nav = NAVS.find(n => n.label === label);
      return nav && disabledKeys.has(nav.key) ? (
        <span className='disabled-nav-tab'>{label}</span>
      ) : (
        <span>{label}</span>
      );
    },
    [disabledKeys]
  );

  const [primaryDateRange, setPrimaryDateRange] = useState<DateRange>(defaultPrimaryDateRange);
  const [comparisonDateRange, setComparisonDateRange] = useState<DateRange>(
    defaultComparisonDateRange
  );

  const downloadTable = useCallback(
    (fileNameSubject: string, dataMap: Parameters<typeof commerceOpsTableDownload>[2]) =>
      commerceOpsTableDownload(fileNameSubject, primaryDateRange, dataMap),
    [primaryDateRange]
  );

  const { data: tabData, error: tabError } = useQuery<
    OverviewData | InventoryData | KeywordData | BuyboxData
  >({
    meta: { suppressGlobalError: true },
    queryKey: [
      'commerce-ops-tab',
      tab,
      cid,
      selectedClientName,
      selectedPartnerType,
      primaryDateRange,
      comparisonDateRange
    ],
    // clientName and partnerAccount are defined whenever `enabled` is true.
    queryFn: () =>
      TAB_FETCHERS[tab](
        cid,
        selectedClientName!,
        selectedPartnerType!,
        primaryDateRange,
        comparisonDateRange
      ),
    enabled: Boolean(selectedClientName && selectedPartnerType)
  });

  useEffect(() => {
    if (tabError) {
      setError({ message: `Error fetching ${tab} data`, reportError: tabError });
    }
  }, [tabError, tab, setError]);

  const pageContent = useMemo(() => {
    if (clientsError || (!isFetchingAccount && commerceClientOptions.length === 0)) {
      return (
        <EmptyState
          title={clientsError ? 'Failed to load commerce accounts' : 'No commerce accounts'}
          subtitle={
            clientsError
              ? 'Refresh the page to try again.'
              : 'This company has no Commerce Ops accounts connected.'
          }
        />
      );
    }
    if (isFetchingAccount || !tabData || !uiConfig) {
      return <FullPageSpinner />;
    }
    switch (tab) {
      case PageTab.OVERVIEW:
        return (
          <Overview
            data={tabData as Parameters<typeof Overview>[0]['data']}
            downloadTable={downloadTable}
            config={uiConfig[PageTab.OVERVIEW]}
          />
        );
      case PageTab.INVENTORY:
        return (
          <Inventory
            data={tabData as Parameters<typeof Inventory>[0]['data']}
            downloadTable={downloadTable}
            primaryDateRange={primaryDateRange}
            config={uiConfig[PageTab.INVENTORY]}
          />
        );
      case PageTab.KEYWORDS_AND_TRAFFIC:
        return (
          <Keyword
            data={tabData as Parameters<typeof Keyword>[0]['data']}
            downloadTable={downloadTable}
          />
        );
      default:
        return (
          <Buybox
            data={tabData as Parameters<typeof Buybox>[0]['data']}
            downloadTable={downloadTable}
          />
        );
    }
  }, [
    clientsError,
    commerceClientOptions.length,
    isFetchingAccount,
    tab,
    tabData,
    downloadTable,
    primaryDateRange,
    uiConfig
  ]);

  const [showGlossary, setShowGlossary] = useState(false);

  return (
    <Page
      pageType='Commerce Ops'
      title='Commerce Ops'
      navs={NAVS}
      selectedNav={tab}
      onNav={handleGoToTab}
      navRenderer={navRenderer}
      actions={
        <div className='commerce-ops-actions'>
          {isFetchingAccount && (
            <Dropdown
              className='client-select-loading'
              ariaLabel='Client'
              size={Size.MD}
              options={[]}
              onChange={noop}
              disabled
            />
          )}
          {showSubheader && (
            <>
              <div className='client-select'>
                <Dropdown
                  ariaLabel='Client'
                  size={Size.MD}
                  fitMenuToContent
                  value={selectedClientName}
                  options={clientNames}
                  onChange={val => {
                    const newClient = commerceClientOptions.find(acct => acct.clientName === val);
                    if (!newClient) {
                      return;
                    }
                    setSelectedPartnerType(
                      newClient.partnerAccounts.find(
                        acct => acct.partnerType === selectedPartnerType?.partnerType
                      ) ?? newClient.partnerAccounts[0]
                    );
                    setSelectedClientName(val);
                  }}
                />
                {/* Hidden copies of the toggle for every client share the grid cell, so it is as wide as the widest. */}
                {clientNames.map(clientName => (
                  <div key={clientName} className='client-select-sizer' aria-hidden>
                    <Dropdown
                      ariaLabel='Client'
                      size={Size.MD}
                      value={clientName}
                      options={clientNames}
                      onChange={noop}
                      stretch={StretchMode.FIT}
                    />
                  </div>
                ))}
              </div>
              {showPartnerAccountToggle && selectedPartnerType && (
                <SegmentedControlButton
                  selectedOption={selectedPartnerType.partnerType}
                  onChange={onToggle}
                  options={availablePartnerAccounts}
                  size={Size.MD}
                  background={Background.DARK}
                />
              )}
            </>
          )}
          <Button
            className='glossary-button'
            leadingElement={<MdOutlineMenuBook />}
            onClick={() => setShowGlossary(true)}
            size={Size.MD}
          >
            Glossary
          </Button>
          <DatePicker
            range={primaryDateRange}
            onChange={setPrimaryDateRange}
            comparison
            datePresets
            comparisonRange={comparisonDateRange}
            comparisonOnChange={setComparisonDateRange}
          />
        </div>
      }
    >
      <div className='commerce-ops-page'>
        <div>{pageContent}</div>
      </div>
      <GlossaryModal
        show={showGlossary}
        setShow={setShowGlossary}
        title='Commerce Ops Glossary'
        initialGlossary={GLOSSARY}
      />
    </Page>
  );
};

export { CommerceOps };
